import React, { useCallback, useRef, useState } from 'react'
import {
  ActionIcon,
  Box,
  Group,
  Image,
  Loader,
  SegmentedControl,
  Text,
  TextInput,
} from '@mantine/core'
import { Upload, X } from 'lucide-react'
import { notifications } from '@mantine/notifications'
import { uploadFile } from '../api'

/**
 * value — строка URL изображения или пустая строка
 * onChange(url) — вызывается при смене URL
 */
export default function ImageUploader({ value, onChange, label }) {
  const [mode, setMode] = useState(value?.startsWith('/uploads') || !value ? 'file' : 'url')
  const [uploading, setUploading] = useState(false)
  const [dragging, setDragging] = useState(false)
  const inputRef = useRef(null)

  const handleFile = useCallback(
    async (file) => {
      if (!file) return
      setUploading(true)
      try {
        const data = await uploadFile(file)
        onChange(data.url)
        setMode('file')
      } catch (err) {
        notifications.show({ color: 'red', title: 'Ошибка загрузки', message: err.message })
      } finally {
        setUploading(false)
      }
    },
    [onChange]
  )

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault()
      setDragging(false)
      const file = e.dataTransfer.files[0]
      if (file?.type.startsWith('image/')) handleFile(file)
    },
    [handleFile]
  )

  return (
    <Box>
      {label && (
        <Text size="sm" fw={500} mb={6}>
          {label}
        </Text>
      )}

      <SegmentedControl
        size="xs"
        mb="sm"
        value={mode}
        onChange={setMode}
        data={[
          { label: 'Загрузить файл', value: 'file' },
          { label: 'Вставить URL', value: 'url' },
        ]}
      />

      {mode === 'url' ? (
        <Group gap="xs" align="flex-end">
          <TextInput
            style={{ flex: 1 }}
            placeholder="https://..."
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
          />
          {value && (
            <ActionIcon variant="light" color="red" onClick={() => onChange('')}>
              <X size={14} />
            </ActionIcon>
          )}
        </Group>
      ) : (
        <Box
          onDragOver={(e) => { e.preventDefault(); setDragging(true) }}
          onDragLeave={() => setDragging(false)}
          onDrop={handleDrop}
          onClick={() => !uploading && inputRef.current?.click()}
          style={{
            border: `2px dashed ${dragging ? '#339af0' : '#dee2e6'}`,
            borderRadius: 8,
            padding: '16px 12px',
            cursor: uploading ? 'default' : 'pointer',
            textAlign: 'center',
            background: dragging ? '#e7f5ff' : undefined,
            transition: 'border-color .15s, background .15s',
          }}
        >
          {uploading ? (
            <Loader size="sm" />
          ) : (
            <Group justify="center" gap="xs">
              <Upload size={16} color="#868e96" />
              <Text size="sm" c="dimmed">
                Перетащите или нажмите для выбора
              </Text>
            </Group>
          )}
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            style={{ display: 'none' }}
            onChange={(e) => handleFile(e.target.files[0])}
          />
        </Box>
      )}

      {value && (
        <Box mt="sm" style={{ position: 'relative', display: 'inline-block' }}>
          <Image
            src={value}
            alt="preview"
            h={100}
            w="auto"
            radius="sm"
            style={{ maxWidth: 200, objectFit: 'cover' }}
          />
          <ActionIcon
            size="xs"
            color="red"
            variant="filled"
            style={{ position: 'absolute', top: 4, right: 4 }}
            onClick={(e) => { e.stopPropagation(); onChange('') }}
          >
            <X size={10} />
          </ActionIcon>
        </Box>
      )}
    </Box>
  )
}
